/**
 * Settling a confirmed operation: how server truth reaches `base` before the pending overlay
 * drops.
 *
 * The overlay stops applying the moment its mutation leaves `pending`. If `base` doesn't already
 * reflect the write at that instant, the row snaps back to pre-mutation data and jumps forward
 * again when a refetch lands — so the settle has to run *before* the mutation resolves, and the
 * producer has to await it.
 *
 * Consumers were hand-rolling this and had already diverged on three details that are easy to get
 * wrong and invisible when wrong:
 *
 * - **Cancel first.** A fetch already in flight was issued against pre-mutation state; if it
 *   resolves after the fold it reinstates stale data.
 * - **Fold without advancing `dataUpdatedAt`.** The fold is a local write of a confirmed value,
 *   not a fresh read. Letting it bump the timestamp tells TanStack the data is newer than it is
 *   and suppresses the revalidation that would reconcile everything the operation doesn't carry.
 * - **Re-fold after revalidation.** A refetch that raced the mutation response can land between
 *   the fold and the resolve, reverting the confirmed value while the overlay is already gone.
 */
import type {QueryClient, QueryKey} from '@github-ui/react-query'

import {
  applyDocumentOperation,
  type ConnectionOperation,
  type DocumentAdapter,
  type EntityFieldItemApplier,
  type EntityFieldMapper,
  type EntityFieldValuesDeltaApplier,
  type EntityFieldItemsUpdater,
  operationNeedsIncorporation,
  resolveDocumentAdapter,
  type SetFieldsMapper,
} from './connection-operations'

export type SettleTarget = {
  queryKey: QueryKey
  /** This cache's representation, not necessarily the document rendered by the subscribing view. */
  documentAdapter?: DocumentAdapter
  /**
   * Passed through to `invalidateQueries`. Defaults to TanStack's `'active'`; use `'all'` when a
   * cache must be refetched even while nothing is subscribed to it — e.g. a list query behind an
   * open side panel, which would otherwise come back stale when the panel closes.
   */
  refetchType?: 'active' | 'inactive' | 'all'
  /**
   * Revalidate this key after folding, to reconcile server-derived fields the operation doesn't
   * carry (timestamps, counts, pagination). Awaited, so the overlay outlives it.
   */
  revalidate?: boolean
}

/**
 * Folds `operation` into each target's cached document, then optionally revalidates and folds
 * again. Returns a promise the producer must await before its mutation leaves `pending`.
 */
export async function settleConfirmedOperation({
  queryClient,
  targets,
  ownerId,
  operation,
  identityPath,
  mapFields,
  mapEntityField,
  applyEntityFieldItem,
  applyEntityFieldValuesDelta,
  updateEntityFieldItems,
  operationId,
  incorporateOperation = false,
  forceRevalidate = false,
}: {
  queryClient: QueryClient
  targets: SettleTarget[]
  ownerId: string
  operation: ConnectionOperation
  /** Must match the identity the document's overlay uses, or the fold lands nowhere. */
  identityPath?: string[]
  mapFields?: SetFieldsMapper
  mapEntityField?: EntityFieldMapper
  /** Must match the semantic entity-field item applier used by the pending overlay. */
  applyEntityFieldItem?: EntityFieldItemApplier
  /** Must match the semantic aggregate applier used by the pending overlay. */
  applyEntityFieldValuesDelta?: EntityFieldValuesDeltaApplier
  /** Must match the semantic capped-list updater used by the pending overlay. */
  updateEntityFieldItems?: EntityFieldItemsUpdater
  /** Correlates a confirmed fold with the still-pending optimistic operation. */
  operationId?: string
  /** The confirmed operation already contains the pending operation's effect. */
  incorporateOperation?: boolean
  /** Refetch every affected cache when concurrent relative operations are not monotonic. */
  forceRevalidate?: boolean
}): Promise<void> {
  if (targets.length === 0) return
  const defaultAdapter = resolveDocumentAdapter({
    identityPath,
    mapFields,
    mapEntityField,
    applyEntityFieldItem,
    applyEntityFieldValuesDelta,
    updateEntityFieldItems,
  })
  const selectedTargets = targets.map(target => ({
    ...target,
    documentAdapter: target.documentAdapter ?? defaultAdapter,
  }))
  const change = {ownerId, operation, operationId}
  const context = {
    incorporateOperation: Boolean(operationId) && (incorporateOperation || operationNeedsIncorporation(operation)),
  }

  // Entity operations arrive from a bridge that deliberately doesn't filter by owner — a
  // `setFields` names its own target, so one mount serves every entity a document renders. That
  // means this runs for operations targeting entities this document has never heard of, and
  // cancelling and refetching for those would make every field edit anywhere in the app cost a
  // refetch here.
  //
  // Affectedness is the test rather than "did the fold change anything": a document can hold the
  // entity while selecting none of the operation's fields, yet still select server-derived ones it
  // affects (`closedAt`, `lastEditedAt`), which only a refetch reconciles.
  // Read before cancellation, which clears fetching state. A registered deferred target can still
  // have no data while its first request is in flight; it must be restarted so that stale response
  // cannot land after the optimistic operation drops.
  const wasFetching = new Map(
    selectedTargets.map(target => [target, queryClient.isFetching({queryKey: target.queryKey}) > 0] as const),
  )
  const affected = selectedTargets.filter(target => {
    const base = queryClient.getQueryData(target.queryKey)
    return base === undefined
      ? wasFetching.get(target)
      : applyDocumentOperation(base, change, target.documentAdapter, context) !== undefined
  })
  if (affected.length === 0) return

  await Promise.all(affected.map(target => queryClient.cancelQueries({queryKey: target.queryKey})))
  const applied = new Set<SettleTarget>()
  // Another confirmation can settle while cancellation is awaited. Read its latest write.
  for (const target of affected) {
    if (foldInto(target)) applied.add(target)
  }

  const skipsConfiguredRevalidation =
    operation.type === 'upsertEntityFieldItem' ||
    operation.type === 'removeEntityFieldItem' ||
    operation.type === 'deltaEntityFieldValues' ||
    operation.type === 'updateEntityFieldItems' ||
    ('counts' in operation && Boolean(operation.counts?.length))
  const revalidated = affected.filter(target => {
    if (forceRevalidate) return wasFetching.get(target) || applied.has(target) || target.revalidate
    if (queryClient.getQueryData(target.queryKey) === undefined) return wasFetching.get(target)
    if (skipsConfiguredRevalidation) return wasFetching.get(target)
    return wasFetching.get(target) || target.revalidate
  })
  const revalidations = revalidated
    // Keyed operations carry a complete replacement/removal for their item. Refetching here can
    // return a pre-write collection and erase another key that just settled; the producer's
    // confirmed operation is the authoritative value for this item. Aggregate and count-only
    // operations, including bulk item updates, are likewise complete for their fields. A fetch
    // that was already in progress is different: settlement cancelled it above, so it must always
    // be restarted.
    .map(target =>
      queryClient.invalidateQueries(
        target.refetchType ? {queryKey: target.queryKey, refetchType: target.refetchType} : {queryKey: target.queryKey},
      ),
    )
  if (revalidations.length === 0) return

  await Promise.all(revalidations)
  // The refetch may have raced the mutation response, so restore the confirmed operation before
  // its pending overlay drops.
  if (!forceRevalidate) {
    for (const target of affected) foldInto(target)
  }

  function foldInto(target: SettleTarget & {documentAdapter: DocumentAdapter}): boolean {
    const base = queryClient.getQueryData(target.queryKey)
    if (base === undefined) return false
    const result = applyDocumentOperation(base, change, target.documentAdapter, context)
    if (!result || result.document === base) return false
    const state = queryClient.getQueryState(target.queryKey)
    queryClient.setQueryData(target.queryKey, result.document, state ? {updatedAt: state.dataUpdatedAt} : undefined)
    return true
  }
}
